import React, { Fragment, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { min, max, uniq } from 'lodash';
import { useParams } from 'react-router-dom';
import { Box, Accordion, AccordionSummary, AccordionDetails, makeStyles, Typography, Grid, Button } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import FilterListIcon from '@material-ui/icons/FilterList';
import { useStores } from '../../hooks/use-stores';
import { filters } from './filters';

const ProductFilter = observer(() => {
    const classes = useStyles();
    const { ProductStore: store } = useStores();
    const { categoryId } = useParams();
    const [expanded, setExpanded] = useState(false);
    let timerInput;

    // Effects
    useEffect(() => {
        store.getFilterFields(categoryId);
    }, [categoryId, store]);

    useEffect(() => {
        if (store.subcategories?.length) {
            const subcategory = {
                name: 'subcategory',
                type: categoryId === '4' ? 'subcategoriesRadio' : 'subcategoriesCheckbox',
            };

            store.setFilterField(subcategory);
        }
    }, [categoryId, store, store.subcategories]);

    useEffect(() => {
        if (!store.loading && store.filterFields.length && store.products.length) {
            setExpanded(true);
        }
    }, [store.loading, store.products, store.filterFields]);

    // Handler
    const accordionToggle = () => {
        setExpanded(prevState => !prevState);
    };

    const handleChange = (name, value, type) => {
        clearTimeout(timerInput);
        timerInput = setTimeout(() => {
            switch (type) {
                case 'range':
                    store.setFilter(`${name}_gte`, value[0]);
                    store.setFilter(`${name}_lte`, value[1]);
                    break;

                case 'checkbox':
                    store.setFilter(`${name}_eqManyToOne`, value);
                    break;

                case 'radio2':
                    store.setFilter(`${name}_eqOneToMany`, value);
                    break;

                default:
                    store.setFilter(`${name}_eq`, value);
                    break;
            }
        }, 300);
    };

    const handleReset = () => {
        window.location.reload();
    };

    // Renders
    const renderFilters = () => {
        return store.filterFields.map(({ name, type }) => {
            switch (type) {
                case 'range':
                    const availableRangeValues = store.products.map(product => parseInt(product.filterProps[name]));
                    const minValue = min(availableRangeValues);
                    const maxValue = max(availableRangeValues);

                    return (
                        <Grid item xs={4}>
                            {filters[name](minValue, maxValue, handleChange)}
                        </Grid>
                    );

                case 'checkbox':
                    const availableCheckboxValues = store.products.map(product => {
                        const filterValue = product.filterProps[name];

                        if (Array.isArray(filterValue)) {
                            return filterValue.toString();
                        }

                        return filterValue;
                    });

                    return (
                        <Grid item xs={4}>
                            {filters[name](uniq(availableCheckboxValues), store.filters[`${name}_eqManyToOne`] || [], handleChange, type)}
                        </Grid>
                    );

                case 'radio':
                    const availableRadioValues = store.products.map(product => {
                        const filterValue = product.filterProps[name];

                        if (Array.isArray(filterValue)) {
                            return filterValue.toString();
                        }

                        return filterValue;
                    });

                    return (
                        <Grid item xs={4}>
                            {filters[name](uniq(availableRadioValues), store.filters[`${name}_eq`], handleChange, type)}
                        </Grid>
                    );

                case 'subcategoriesCheckbox':
                    if (store.subcategories.length) {
                        return (
                            <Grid item xs={4}>
                                {filters[name](store.subcategories, store.filters[`${name}_eqManyToOne`] || [], handleChange, type)}
                            </Grid>
                        );
                    }
                    return null;

                case 'subcategoriesRadio':
                    if (store.subcategories.length) {
                        return (
                            <Grid item xs={4}>
                                {filters[name](store.subcategories, store.filters[`${name}_eqOneToMany`], handleChange, type)}
                            </Grid>
                        );
                    }
                    return null;

                default:
                    return null;
            }
        });
    };

    // If filters for current category is not defined
    if (!store.filterFields?.length) {
        return null;
    }

    return (
        <Box mb={3}>
            <Accordion
                expanded={expanded}
                elevation={0}
                className={classes.filterContainer}
                disabled={!store.products?.length}
                onChange={accordionToggle}
            >
                <AccordionSummary disabled={store.loading} expandIcon={<ExpandMoreIcon />}>
                    <Box width="100%" display="flex">
                        <Box mr={1}>
                            <FilterListIcon />
                        </Box>
                        <Typography>Фильтр</Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails className={classes.filterContent}>
                    <Box flex="1" display="flex" flexDirection="column">
                        <Box flex="1" mb={3}>
                            <Grid container spacing={8}>
                                {!store.loading && store.products.length
                                    ? renderFilters().map((filter, index) => <Fragment key={index}>{filter}</Fragment>)
                                    : null}
                            </Grid>
                        </Box>
                        <Box>
                            <Button variant="outlined" color="primary" size="small" onClick={handleReset}>
                                Сбросить
                            </Button>
                        </Box>
                    </Box>
                </AccordionDetails>
            </Accordion>
        </Box>
    );
});

const useStyles = makeStyles(theme => ({
    filterContainer: {
        borderRadius: '0 !important',
        '& .MuiAccordionSummary-root.Mui-expanded': {
            minHeight: 48,
        },
        '& .MuiAccordionSummary-content.Mui-expanded': {
            margin: '12px 0',
        },
    },
    filterContent: {
        backgroundColor: '#fff',
        padding: theme.spacing(2, 3, 3),
    },
}));

export default ProductFilter;
